import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import VideoCall from "@/components/VideoCall";
import PaymentPanel from "@/components/PaymentPanel";
import Footer from "@/components/Footer";
import {
  HeartPulse, LogOut, Stethoscope, Search, CalendarDays, Clock, Video, IndianRupee,
  ArrowLeft, Loader2, Star, CheckCircle2, X, CreditCard, Phone, ChevronRight,
  Paperclip, FileText, Trash2,
} from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const drName = (n) => { const s = (n || "Doctor").trim(); return /^dr\.?\s/i.test(s) ? s : `Dr. ${s}`; };
const fmtDate = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "");
const to12h = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ap}`;
};
const STATUS_STYLES = {
  booked: "bg-blue-50 text-blue-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-red-50 text-red-600",
};
const PAY_STYLES = {
  pending: "bg-amber-50 text-amber-700",
  claimed: "bg-sky-50 text-sky-700",
  confirmed: "bg-emerald-50 text-emerald-700",
};

export default function PatientApp() {
  const { user, logout, refresh } = useAuth();
  const [tab, setTab] = useState("find");
  const [call, setCall] = useState(null); // {room, name}
  const [switching, setSwitching] = useState(false);

  const switchRole = async (r) => {
    setSwitching(true);
    try {
      await api.post("/role/change", { role: r });
      await refresh();
      toast.success(r === "doctor" ? "Welcome, Doctor" : "Continuing as Doctor's Assistant");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch account type");
    } finally { setSwitching(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBF7]" data-testid="patient-app">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-amber-100/70 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#1E5AA8] flex items-center justify-center shadow-md"><HeartPulse className="w-5 h-5 text-amber-300" /></div>
            <div>
              <h1 className="font-display text-lg font-bold text-[#123C73] leading-none">MDvia Care</h1>
              <p className="text-[11px] text-slate-500 mt-0.5" data-notranslate>{user?.full_name || user?.name || "Patient"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button data-testid="patient-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 px-3 py-2 rounded-xl hover:bg-red-50"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex gap-1">
          {[["find", "Find a Doctor", Stethoscope], ["mine", "My Consultations", CalendarDays]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`patient-tab-${id}`} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === id ? "border-[#1E5AA8] text-[#1E5AA8]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-[1200px] w-full mx-auto px-4 sm:px-6 py-6">
        {tab === "find" ? <FindDoctor onCall={setCall} /> : <MyConsultations onCall={setCall} />}
      </main>

      <div className="max-w-[1200px] w-full mx-auto px-4 sm:px-6 pb-4 text-center">
        <p className="text-xs text-slate-400">
          Are you a doctor or clinic staff?{" "}
          <button data-testid="switch-to-doctor" onClick={() => switchRole("doctor")} disabled={switching}
            className="text-[#1E5AA8] font-medium hover:underline disabled:opacity-50">
            {switching ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Register as a Doctor"}
          </button>
          {" · "}
          <button data-testid="switch-to-assistant" onClick={() => switchRole("assistant")} disabled={switching}
            className="text-[#1E5AA8] font-medium hover:underline disabled:opacity-50">Join as Doctor's Assistant</button>
        </p>
      </div>

      <Footer />
      {call && <VideoCall room={call.room} name={call.name} onClose={() => setCall(null)} />}
    </div>
  );
}

function FindDoctor({ onCall }) {
  const [specialties, setSpecialties] = useState([]);
  const [specialty, setSpecialty] = useState("");
  const [q, setQ] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/teleconsult/specialties").then((r) => setSpecialties(r.data.specialties || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/doctors", { params: { specialty: specialty || undefined, q: q || undefined } })
      .then((r) => setDoctors(r.data)).catch(() => setDoctors([])).finally(() => setLoading(false));
  }, [specialty, q]);

  if (selected) return <DoctorDetail doctor={selected} onBack={() => setSelected(null)} onCall={onCall} />;

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-slate-900">Book an online consultation</h2>
      <p className="text-slate-500 text-sm mt-1">Choose a specialty, pick a doctor, and consult by secure video — from home.</p>

      <div className="mt-5 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input data-testid="doctor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search doctor or specialty…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Chip active={specialty === ""} onClick={() => setSpecialty("")} label="All specialties" testId="spec-all" />
        {specialties.map((s) => (
          <Chip key={s.name} active={specialty === s.name} onClick={() => setSpecialty(s.name)} label={`${s.name} (${s.count})`} testId={`spec-${s.name}`} />
        ))}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#1E5AA8] animate-spin" /></div>
        ) : doctors.length === 0 ? (
          <div className="col-span-full text-center py-16 text-slate-400" data-testid="no-doctors">No doctors available online for this search yet.</div>
        ) : doctors.map((d, i) => (
          <motion.div key={d.doctor_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            data-testid={`doctor-card-${d.doctor_id}`} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 flex flex-col">
            <div className="flex items-center gap-3">
              {d.picture ? <img src={d.picture} alt="" className="w-14 h-14 rounded-2xl object-cover" /> : <div className="w-14 h-14 rounded-2xl bg-[#1E5AA8] text-amber-300 flex items-center justify-center text-xl font-bold" data-notranslate>{(d.name || "D")[0]}</div>}
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate" data-notranslate>{drName(d.name)}</p>
                <p className="text-xs text-slate-500 truncate">{d.specialty || "General Physician"}</p>
                {d.qualifications && <p className="text-[11px] text-slate-400 truncate" data-notranslate>{d.qualifications}</p>}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <span className="inline-flex items-center text-[#123C73] font-bold" data-notranslate><IndianRupee className="w-4 h-4" />{(d.fee || 0).toLocaleString("en-IN")}</span>
              <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Online</span>
            </div>
            <button data-testid={`book-doctor-${d.doctor_id}`} onClick={() => setSelected(d)}
              className="mt-4 w-full inline-flex items-center justify-center gap-1 py-2.5 rounded-xl bg-[#1E5AA8] hover:bg-[#123C73] text-white text-sm font-semibold">
              Book appointment <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DoctorDetail({ doctor, onBack, onCall }) {
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [booking, setBooking] = useState(false);
  const [payFor, setPayFor] = useState(null);

  useEffect(() => {
    setLoadingSlots(true); setSlot("");
    api.get(`/doctors/${doctor.doctor_id}/slots`, { params: { date } })
      .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  }, [date, doctor.doctor_id]);

  const book = async () => {
    if (!slot) { toast.error("Please pick a time slot"); return; }
    setBooking(true);
    try {
      const r = await api.post("/teleconsults", { doctor_id: doctor.doctor_id, date, time: slot, reason, patient_phone: phone });
      toast.success("Appointment booked!");
      setPayFor(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  };

  return (
    <div data-testid="doctor-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" /> Back to doctors</button>

      <div className="mt-4 rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-4">
          {doctor.picture ? <img src={doctor.picture} alt="" className="w-16 h-16 rounded-2xl object-cover" /> : <div className="w-16 h-16 rounded-2xl bg-[#1E5AA8] text-amber-300 flex items-center justify-center text-2xl font-bold" data-notranslate>{(doctor.name || "D")[0]}</div>}
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900" data-notranslate>{drName(doctor.name)}</h2>
            <p className="text-sm text-slate-500">{doctor.specialty || "General Physician"}</p>
            <span className="inline-flex items-center mt-1 text-[#123C73] font-bold" data-notranslate><IndianRupee className="w-4 h-4" />{(doctor.fee || 0).toLocaleString("en-IN")} consultation fee</span>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Select date</label>
            <input data-testid="slot-date" type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Your phone (so the doctor can reach you)</label>
            <input data-testid="patient-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-slate-500 mb-2 block">Available slots on {fmtDate(date)}</label>
          {loadingSlots ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 text-[#1E5AA8] animate-spin" /></div>
          ) : slots.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="no-slots">No slots available on this day. Try another date.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="slot-grid">
              {slots.map((s) => (
                <button key={s} data-testid={`slot-${s}`} onClick={() => setSlot(s)}
                  className={`py-2 rounded-lg text-sm border transition-colors ${slot === s ? "bg-[#1E5AA8] text-white border-[#1E5AA8]" : "border-slate-200 text-slate-700 hover:border-[#1E5AA8]"}`}>
                  {to12h(s)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-slate-500 mb-1 block">Reason for consultation (optional)</label>
          <textarea data-testid="consult-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            placeholder="e.g. Fever and cough for 3 days" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
        </div>

        <button data-testid="confirm-booking" onClick={book} disabled={booking || !slot}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-[#123C73] font-semibold disabled:opacity-60">
          {booking ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Confirm & proceed to pay
        </button>
      </div>

      {payFor && <PaymentPanel tc={payFor} onPaid={() => { setPayFor(null); onBack(); }} onClose={() => { setPayFor(null); onBack(); }} />}
    </div>
  );
}

function MyConsultations({ onCall }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [payFor, setPayFor] = useState(null);

  const load = () => api.get("/teleconsults").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try { await api.post(`/teleconsults/${id}/cancel`); toast.success("Consultation cancelled"); load(); }
    catch { toast.error("Could not cancel"); }
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#1E5AA8] animate-spin" /></div>;
  if (items.length === 0) return <div className="text-center py-16 text-slate-400" data-testid="no-consults">No consultations yet. Book one from "Find a Doctor".</div>;

  return (
    <div className="space-y-3" data-testid="my-consults">
      <h2 className="font-display text-2xl font-bold text-slate-900">My consultations</h2>
      {items.map((t) => (
        <div key={t.id} data-testid={`consult-${t.id}`} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-900" data-notranslate>{drName(t.doctor_name)}</p>
              <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[t.status] || "bg-slate-100 text-slate-600"}`}>{t.status}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${PAY_STYLES[t.payment_status] || "bg-slate-100 text-slate-600"}`}>{t.payment_status === "pending" ? "Payment due" : t.payment_status === "claimed" ? "Payment sent" : "Paid"}</span>
            </div>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><CalendarDays className="w-4 h-4" />{fmtDate(t.date)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{to12h(t.time)}</span>
              <span>{t.specialty}</span>
            </p>
            <ReportBox tc={t} onChange={load} />
          </div>
          <div className="flex items-center gap-2">
            {t.payment_status === "pending" && t.status === "booked" && (
              <button data-testid={`pay-${t.id}`} onClick={() => setPayFor(t)} className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl"><CreditCard className="w-4 h-4" /> Pay now</button>
            )}
            {t.status === "booked" && (
              <>
                <button data-testid={`join-${t.id}`} onClick={() => onCall({ room: t.room, name: user?.full_name || user?.name || "Patient" })} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-[#1E5AA8] hover:bg-[#123C73] px-3 py-2 rounded-xl"><Video className="w-4 h-4" /> Join video</button>
                <button data-testid={`cancel-${t.id}`} onClick={() => cancel(t.id)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-red-600 px-2 py-2 rounded-xl"><X className="w-4 h-4" /></button>
              </>
            )}
          </div>
        </div>
      ))}
      {payFor && <PaymentPanel tc={payFor} onPaid={() => { setPayFor(null); load(); }} onClose={() => { setPayFor(null); load(); }} />}
    </div>
  );
}

function Chip({ active, onClick, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[#1E5AA8] text-white border-[#1E5AA8]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1E5AA8]"}`}>
      {label}
    </button>
  );
}

function ReportBox({ tc, onChange }) {
  const [busy, setBusy] = useState(false);
  const reports = tc.reports || [];
  const canUpload = tc.status === "booked";

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/teleconsults/${tc.id}/reports`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report attached — your doctor can review it before the call");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (file_id) => {
    try { await api.delete(`/teleconsults/${tc.id}/reports/${file_id}`); onChange(); }
    catch { toast.error("Could not remove report"); }
  };

  return (
    <div className="mt-2" data-testid={`report-box-${tc.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {reports.map((rep) => (
          <span key={rep.file_id} className="inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-1 rounded-full bg-[#EBF3FB] text-[#1E5AA8]">
            <a href={`${API}/teleconsults/${tc.id}/reports/${rep.file_id}`} target="_blank" rel="noreferrer" data-testid="patient-report-link"
              className="inline-flex items-center gap-1 hover:underline" data-notranslate>
              <FileText className="w-3 h-3" /> {rep.filename}
            </a>
            {canUpload && (
              <button onClick={() => remove(rep.file_id)} data-testid="patient-report-remove" title="Remove" className="p-0.5 text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
            )}
          </span>
        ))}
        {canUpload && reports.length < 8 && (
          <label data-testid={`attach-report-${tc.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${busy ? "opacity-50 pointer-events-none" : "border-[#1E5AA8]/40 text-[#1E5AA8] hover:bg-[#EBF3FB] cursor-pointer"}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Attach report
            <input type="file" accept=".pdf,image/jpeg,image/png,image/jpg" className="hidden" onChange={onFile} disabled={busy} />
          </label>
        )}
      </div>
      {canUpload && reports.length === 0 && (
        <p className="text-[11px] text-slate-400 mt-1">Attach your lab reports (PDF or photo) so the doctor can review them before your video call.</p>
      )}
    </div>
  );
}
